// ============================================
// NEW DAY BANNER COMPONENT
// Shows "New day started" notification after midnight reset
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Sun, X, ChevronRight, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const NewDayBanner = () => {
    const { api, user } = useAuth();
    const navigate = useNavigate();
    const [cycleData, setCycleData] = useState(null);
    const [visible, setVisible] = useState(false);
    const [dismissing, setDismissing] = useState(false);

    // Fetch current cycle data
    const fetchCycleData = useCallback(async () => {
        if (!user) return;
        
        try {
            const response = await api().get('/daily-reports/current-cycle');
            const data = response.data;
            
            setCycleData(data);
            
            // Show banner if new day notification exists and not dismissed
            if (data.show_new_day_banner) {
                setVisible(true);
            }
        } catch (error) {
            console.error('Error fetching cycle data:', error);
        }
    }, [api, user]);

    useEffect(() => {
        fetchCycleData();
        
        // Check every 5 minutes
        const interval = setInterval(fetchCycleData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchCycleData]);

    // Dismiss banner
    const dismissBanner = async () => {
        setDismissing(true);
        try {
            await api().post('/daily-reports/dismiss-new-day');
            setVisible(false);
        } catch (error) {
            console.error('Error dismissing banner:', error);
        } finally {
            setDismissing(false);
        }
    };

    // Go to daily reports
    const viewReports = () => {
        navigate('/daily-reports');
        dismissBanner();
    };

    if (!visible || !cycleData?.new_day_data) return null;

    const { new_day_data } = cycleData;
    const hasAnomalies = new_day_data.has_anomalies;

    return (
        <div className={`fixed top-0 left-0 right-0 z-50 ${
            hasAnomalies 
                ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
                : 'bg-gradient-to-r from-blue-500 to-indigo-500'
        } text-white shadow-lg`}>
            <div className="max-w-7xl mx-auto px-4 py-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${
                            hasAnomalies ? 'bg-amber-400/30' : 'bg-white/20'
                        }`}>
                            {hasAnomalies ? (
                                <AlertTriangle className="w-5 h-5" />
                            ) : (
                                <Sun className="w-5 h-5" />
                            )}
                        </div>
                        <div>
                            <p className="font-semibold">
                                {new_day_data.message || '🌅 Nouveau jour démarré'}
                            </p>
                            {new_day_data.previous_revenue !== undefined && (
                                <p className="text-sm opacity-90">
                                    CA veille : {new_day_data.previous_revenue?.toLocaleString('fr-FR')} F
                                    {hasAnomalies && ' • ⚠️ Anomalies détectées'}
                                </p>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <button
                            onClick={viewReports}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
                        >
                            Voir le rapport
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={dismissBanner}
                            disabled={dismissing}
                            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewDayBanner;
